import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Edit, Trash2, ArrowLeft, Plus, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from './ui/dialog';
import { toast } from './ui/use-toast';
import { AppBar } from './ui/app-bar';
import { useIsAndroid } from '../hooks/use-android';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { useCompanyState } from '../hooks/useCompanyState';
import SupplierInvoicesPayments from './SupplierInvoicesPayments';
import { getBaseUrl } from '@/lib/api';
import { sanitizeGstInput, GST_VALIDATION_MESSAGE } from '@/lib/utils';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';

interface Supplier {
  id?: number;
  Id?: number;
  ID?: number;
  supplierId?: number;
  SupplierId?: number;
  SupplierName: string;
  Phone?: string;
  Email?: string;
  Address?: string;
  State?: string;
  CreatedOn?: string;
  ContactPerson?: string;
  ContactMobile?: string;
  GSTN?: string;
  Purchases?: number;
  Paid?: number;
  Balance?: number;
  // Id is present but should not be shown
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];

const initialSupplier = {
  Name: '',
  Email: '',
  ContactMobile: '',
  Phone: '',
  ContactName: '',
  GSTN: '',
  Address: '',
  State: '',
};

function formatCurrency(val?: number) {
  if (typeof val !== 'number') return '-';
  return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateDMY(dateStr?: string) {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd-MM-yyyy');
  } catch {
    return dateStr;
  }
}

// Helper function to get supplier ID from various possible field names
function getSupplierId(supplier: any): number | undefined {
  const id = supplier.id || supplier.Id || supplier.ID || supplier.supplierId || supplier.SupplierId;
  console.log('Supplier ID lookup:', {
    supplier: supplier.SupplierName,
    id: supplier.id,
    Id: supplier.Id,
    ID: supplier.ID,
    supplierId: supplier.supplierId,
    SupplierId: supplier.SupplierId,
    result: id
  });
  return id;
}

const fetchStates = async () => {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${baseUrl}/api/States`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch states');
  return response.json();
};

const SupplierPage = ({ token, onBack }: { token: string; onBack?: () => void }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileLoadedCount, setMobileLoadedCount] = useState(pageSize);
  const [isMobileLoading, setIsMobileLoading] = useState(false);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState({ ...initialSupplier });
  const [selectedSuppliers, setSelectedSuppliers] = useState<Set<number>>(new Set());
  const [showInvoicesPayments, setShowInvoicesPayments] = useState(false);

  // Custom back navigation handler for SupplierPage
  // When form/modal open: close it first. Else when showInvoicesPayments: go to supplier list.
  const handleSupplierPageBack = () => {
    console.log('🔙 Supplier Page Back Navigation Triggered', { showModal, showDeleteConfirm, showInvoicesPayments });
    if (showModal) {
      setShowModal(false);
      setIsEdit(false);
      return;
    }
    if (showDeleteConfirm) {
      setShowDeleteConfirm(false);
      setDeleteId(undefined);
      return;
    }
    if (showInvoicesPayments) {
      setSelectedSuppliers(new Set());
      setShowInvoicesPayments(false);
      return;
    }
    if (onBack) {
      console.log('🔙 Using provided onBack callback');
      onBack();
    } else {
      console.log('🔙 Navigating to dashboard with transactions tab');
      navigate('/?tab=transactions');
    }
  };

  // Use back navigation hook with custom handler
  // Note: This will be overridden by Dashboard's back navigation when activeSubpage is set
  useBackNavigation({
    onBack: handleSupplierPageBack,
    canGoBack: true,
    showExitConfirmation: false // Don't show exit confirmation on this page
  });
  const [editId, setEditId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | undefined>(undefined);
  const [isDeleting, setIsDeleting] = useState(false);
  const [gstError, setGstError] = useState(false);
  const queryClient = useQueryClient();
  const isAndroid = useIsAndroid();
  const { companyState } = useCompanyState();
  const { hasPrivilege } = useUserPrivileges();
  const canViewSupplier = hasPrivilege(PRIVILEGE.SUPPLIER_VIEW);
  const canCreateSupplier = hasPrivilege(PRIVILEGE.SUPPLIER_CREATION);
  const canUpdateSupplier = hasPrivilege(PRIVILEGE.SUPPLIER_UPDATE);
  const canDeleteSupplier = hasPrivilege(PRIVILEGE.SUPPLIER_DELETE);

  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', searchQuery, pageSize, currentPage],
    enabled: canViewSupplier,
    queryFn: async () => {
      const response = await fetch(`${baseUrl}/api/Suppliers`, {
        headers: { Authorization: authToken, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch suppliers');
      return response.json();
    },
    staleTime: 0,
  });

  const suppliers: Supplier[] = data?.data || [];
  // Filter and paginate client-side (API does not support search/pagination)
  const filtered = suppliers.filter(s =>
    (s.SupplierName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.Email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.Phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.ContactPerson?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.ContactMobile?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.GSTN?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );
  
  // Debug: Log first supplier to see the actual structure
  if (suppliers.length > 0 && !isLoading) {
    console.log('First supplier data structure:', suppliers[0]);
  }
  const totalRecords = filtered.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const { data: statesResponse, isLoading: isStatesLoading, error: statesError } = useQuery({
    queryKey: ['supplier-states'],
    queryFn: fetchStates,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const states = statesResponse?.data || [];

  // Enhanced lazy loading specifically for Android devices
  const loadMoreItems = useCallback(() => {
    if (isMobileLoading || mobileLoadedCount >= filtered.length) {
      console.log('LoadMoreItems blocked:', {
        isMobileLoading,
        mobileLoadedCount,
        filteredLength: filtered.length
      });
      return;
    }
    
    console.log('Loading more items:', {
      current: mobileLoadedCount,
      total: filtered.length,
      isAndroid
    });
    
    setIsMobileLoading(true);
    // Use a shorter delay for Android for better responsiveness
    const delay = isAndroid ? 100 : 300;
    
    // Calculate optimal batch size for Android
    const batchSize = isAndroid ? Math.min(pageSize, 10) : pageSize;
    
    setTimeout(() => {
      setMobileLoadedCount(count => {
        const newCount = Math.min(count + batchSize, filtered.length);
        console.log('Updated mobileLoadedCount:', { old: count, new: newCount });
        return newCount;
      });
      setIsMobileLoading(false);
    }, delay);
  }, [isMobileLoading, mobileLoadedCount, filtered.length, pageSize, isAndroid]);

  // Improved scroll handler with better detection for Android
  const handleScroll = useCallback(() => {
    if (window.innerWidth >= 1024) return; // Desktop - no lazy loading
    if (searchQuery) return; // No lazy loading if searching
    if (isMobileLoading || mobileLoadedCount >= filtered.length) return;

    // Get scroll position from the mobile container or window
    const mobileContainer = mobileListRef.current;
    let scrollTop, scrollHeight, clientHeight;
    
    if (mobileContainer) {
      scrollTop = mobileContainer.scrollTop;
      scrollHeight = mobileContainer.scrollHeight;
      clientHeight = mobileContainer.clientHeight;
    } else {
      scrollTop = window.scrollY;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    }
    
    // More aggressive loading threshold for Android (load earlier)
    const threshold = isAndroid ? 200 : 100;
    
    // Check if we're near the bottom
    if (scrollHeight - (scrollTop + clientHeight) < threshold) {
      console.log('Scroll trigger:', {
        scrollTop,
        clientHeight,
        scrollHeight,
        threshold,
        mobileLoadedCount,
        filteredLength: filtered.length,
        isAndroid,
        container: !!mobileContainer
      });
      loadMoreItems();
    }
  }, [searchQuery, isMobileLoading, mobileLoadedCount, filtered.length, loadMoreItems, isAndroid]);

  // Throttled scroll handler for better performance
  const throttledScrollHandler = useCallback(() => {
    let ticking = false;
    
    return () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
  }, [handleScroll]);

  // Set up scroll listener with Android-specific optimizations
  useEffect(() => {
    if (window.innerWidth >= 1024) return; // Desktop - no lazy loading
    if (searchQuery) return; // No lazy loading if searching
    
    const scrollHandler = throttledScrollHandler();
    
    // Use the mobile list container for better scroll detection
    const mobileContainer = mobileListRef.current;
    if (mobileContainer) {
      mobileContainer.addEventListener('scroll', scrollHandler, { passive: true });
      return () => {
        mobileContainer.removeEventListener('scroll', scrollHandler);
      };
    } else {
      // Fallback to window scroll
      window.addEventListener('scroll', scrollHandler, { passive: true });
      return () => {
        window.removeEventListener('scroll', scrollHandler);
      };
    }
  }, [throttledScrollHandler, searchQuery]);

  // Cleanup effect to clear cache when component unmounts
  useEffect(() => {
    return () => {
      // Clear supplier specific queries when component unmounts
      queryClient.removeQueries({ queryKey: ['suppliers'] });
      queryClient.removeQueries({ queryKey: ['supplier-states'] });
    };
  }, [queryClient]);

  // Reset loaded count when data or search changes
  useEffect(() => {
    setMobileLoadedCount(pageSize);
    console.log('Reset mobileLoadedCount:', {
      pageSize,
      filteredLength: filtered.length,
      searchQuery
    });
  }, [filtered.length, pageSize, searchQuery]);

  // Handle checkbox selection
  const handleCheckboxChange = (supplierId: number | undefined) => {
    if (!supplierId) return;
    setSelectedSuppliers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  };

  // Handle select all checkboxes
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = paginated
        .map(supplier => getSupplierId(supplier))
        .filter((id): id is number => id !== undefined);
      setSelectedSuppliers(new Set(allIds));
    } else {
      setSelectedSuppliers(new Set());
    }
  };

  // Check if all current page suppliers are selected
  const isAllSelected = paginated.length > 0 && paginated.every(supplier => {
    const id = getSupplierId(supplier);
    return id !== undefined && selectedSuppliers.has(id);
  });

  // Add Supplier
  const handleAdd = () => {
    setForm({ ...initialSupplier, State: companyState || '' });
    setIsEdit(false);
    setEditId(null);
    setShowModal(true);
  };

  // Edit Supplier
  const handleEdit = (supplier: any, id: number | undefined) => {
    console.log('Edit supplier:', supplier);
    console.log('Edit supplier ID:', id);
    if (!id) {
      toast({ title: 'Error', description: 'Invalid supplier ID for editing', variant: 'destructive' });
      return;
    }
    setForm({
      Name: supplier.SupplierName || '',
      Email: supplier.Email || '',
      ContactMobile: supplier.ContactMobile || '',
      Phone: supplier.Phone || '',
      ContactName: supplier.ContactPerson || '',
      GSTN: supplier.GSTN || '',
      Address: supplier.Address || '',
      State: supplier.State || '',
    });
    setIsEdit(true);
    setEditId(id);
    setShowModal(true);
  };

  // Delete Supplier
  const handleDelete = async (id: number | undefined) => {
    console.log('Delete supplier ID:', id);
    if (!id) {
      toast({ title: 'Error', description: 'Invalid supplier ID for deletion', variant: 'destructive' });
      return;
    }
    // Show confirmation dialog
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  // Confirm Delete Supplier
  const confirmDelete = async () => {
    if (!deleteId) {
      toast({ title: 'Error', description: 'Invalid supplier ID for deletion', variant: 'destructive' });
      return;
    }
    setIsDeleting(true);
    try {
      console.log('Deleting supplier with URL:', `${baseUrl}/api/deleteSupplier`);
      const res = await fetch(`${baseUrl}/api/deleteSupplier`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken,
        },
        body: JSON.stringify({ Id: deleteId })
      });
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      console.log('Delete response:', data);
      if (data.status === 0) {
        toast({ title: 'Success', description: data.message });
        queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      } else {
        toast({ title: 'Error', description: data.message || 'Failed to delete supplier', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Delete error:', e);
      toast({ title: 'Error', description: `Failed to delete supplier: ${e instanceof Error ? e.message : 'Unknown error'}`, variant: 'destructive' });
    } finally {
      setShowDeleteConfirm(false);
      setDeleteId(undefined);
      setIsDeleting(false);
    }
  };

  // Handle Invoices & Payments button click
  const handleInvoicesPayments = () => {
    if (selectedSuppliers.size === 0) {
      toast({ 
        title: 'No Supplier Selected', 
        description: 'Please select at least one supplier to view invoices and payments',
        variant: 'destructive' 
      });
      return;
    }
    setShowInvoicesPayments(true);
  };

  // Handle direct supplier click
  const handleSupplierClick = (supplier: Supplier) => {
    const supplierId = getSupplierId(supplier);
    if (!supplierId) {
      toast({ 
        title: 'Error', 
        description: 'Invalid supplier ID',
        variant: 'destructive' 
      });
      return;
    }
    
    console.log('=== Direct Supplier Click Debug ===');
    console.log('Supplier object:', supplier);
    console.log('Supplier Name:', supplier.SupplierName);
    console.log('Extracted supplierId:', supplierId);
    console.log('Supplier State:', supplier.State);
    console.log('Previous selectedSuppliers:', Array.from(selectedSuppliers));
    console.log('All possible ID fields:', {
      id: supplier.id,
      Id: supplier.Id,
      ID: supplier.ID,
      supplierId: supplier.supplierId,
      SupplierId: supplier.SupplierId
    });
    console.log('=== End Direct Supplier Click Debug ===');
    
    // Clear previous selection and set the new supplier
    setSelectedSuppliers(new Set());
    setTimeout(() => {
      setSelectedSuppliers(new Set([supplierId]));
      setShowInvoicesPayments(true);
    }, 0);
  };

  // No permission: user must have Supplier View (53) to see supplier data and tab
  if (!canViewSupplier) {
    return (
      <div className="h-screen flex flex-col lg:space-y-4 lg:p-8">
        <div className="lg:hidden">
          <AppBar title="Supplier Management" onBack={handleSupplierPageBack} showBackButton={true} />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-4">
          <p className="text-muted-foreground text-center">You don&apos;t have permission to view suppliers.</p>
          <Button onClick={handleSupplierPageBack} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        </div>
      </div>
    );
  }

  // If viewing invoices/payments, show that component
  if (showInvoicesPayments) {
    const firstSupplierId = Array.from(selectedSuppliers)[0];
    const firstSupplier = filtered.find(s => getSupplierId(s) === firstSupplierId);
    const supplierState = firstSupplier?.State ?? undefined;
    return (
      <SupplierInvoicesPayments 
        token={token}
        supplierIds={Array.from(selectedSuppliers)}
        supplierState={supplierState}
        onBack={() => {
          console.log('=== Navigating Back from Invoices & Payments ===');
          console.log('Clearing selected suppliers');
          setSelectedSuppliers(new Set());
          setShowInvoicesPayments(false);
        }}
      />
    );
  }

  // Submit Add/Update
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.Name.trim()) {
      toast({ title: 'Error', description: 'Supplier Name is required', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      let res, data;
      let url = `${baseUrl}/api/addSuppliers`;
      let method: 'POST' | 'PUT' = 'POST';
      let body: any = { ...form };
      if (isEdit && editId !== null) {
        url = `${baseUrl}/api/updateSupplier`;
        method = 'PUT';
        body = { ...form, Id: editId };
      }
      console.log(`${isEdit ? 'Updating' : 'Adding'} supplier with URL:`, url);
      console.log('Form data:', body);
      res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      data = await res.json();
      console.log(`${isEdit ? 'Update' : 'Add'} response:`, data);
      if (data.status === 0) {
        toast({ title: 'Success', description: data.message });
        setShowModal(false);
        queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      } else {
        toast({ title: 'Error', description: data.message || `Failed to ${isEdit ? 'update' : 'add'} supplier`, variant: 'destructive' });
      }
    } catch (e) {
      console.error(`${isEdit ? 'Update' : 'Add'} error:`, e);
      toast({ title: 'Error', description: `Failed to ${isEdit ? 'update' : 'add'} supplier: ${e instanceof Error ? e.message : 'Unknown error'}`, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex flex-col lg:space-y-4 lg:p-8 android-form-container">
      {/* Mobile App Bar */}
      <div className="lg:hidden">
        <AppBar 
          title="Supplier Management" 
          onBack={handleSupplierPageBack}
          showBackButton={true}
        />
      </div>
      
      {/* Mobile content with top padding for app bar */}
      <div className="lg:hidden flex flex-col px-4 pb-4 pt-14">
        <div className="flex flex-col gap-2 mb-4">
          <Button
            variant={selectedSuppliers.size > 0 ? "default" : "secondary"}
            onClick={handleInvoicesPayments}
            disabled={selectedSuppliers.size === 0}
            className={selectedSuppliers.size > 0 ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
          >
            Invoices & Payments
          </Button>
        </div>
        <div className="flex items-center">
          <div className="relative flex-1">
            <Input
              placeholder="Search suppliers..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="w-full pr-10"
            />
            {searchQuery && (
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                style={{ zIndex: 2 }}
              >
                ×
              </button>
            )}
          </div>
          {canCreateSupplier && (
            <Button
              variant="default"
              className="ml-2 flex-shrink-0"
              onClick={handleAdd}
            >
              <Plus className="h-5 w-5 mr-1" />
              <span className="hidden sm:inline">Add</span>
            </Button>
          )}
        </div>
      </div>

      {/* Desktop header - normal flow */}
      <div className="hidden lg:block space-y-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-2 mb-2">
            <Button
              onClick={handleSupplierPageBack}
              variant="outline"
              className="flex items-center gap-2 px-3 py-2"
            >
              <span className="hidden lg:flex items-center"><ArrowLeft className="h-5 w-5 mr-2" />Back</span>
            </Button>
            <h1 className="text-lg font-bold ml-2">Supplier Management</h1>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Future: Add Supplier button */}
            <Button
              variant={selectedSuppliers.size > 0 ? "default" : "secondary"}
              onClick={handleInvoicesPayments}
              disabled={selectedSuppliers.size === 0}
              className={selectedSuppliers.size > 0 ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
            >
              Invoices & Payments
            </Button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          {/* Desktop: search + add + page size. Mobile: only add button beside search */}
          <div className="flex w-full max-w-sm items-center">
            <div className="relative flex-1">
              <Input
                placeholder="Search suppliers..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="w-full pr-10"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  style={{ zIndex: 2 }}
                >
                  ×
                </button>
              )}
            </div>
            {canCreateSupplier && (
              <Button
                variant="default"
                className="ml-2 flex-shrink-0"
                onClick={handleAdd}
              >
                <Plus className="h-5 w-5 mr-1" />
                <span className="hidden sm:inline">Add</span>
              </Button>
            )}
          </div>
          {/* Page size dropdown only on desktop */}
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            className="border rounded px-2 py-1 text-sm hidden lg:block"
          >
            {PAGE_SIZE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt} per page</option>
            ))}
          </select>
        </div>
      </div>
      {/* Table view for large screens */}
      <div className="hidden lg:block rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all suppliers"
                />
              </TableHead>
              <TableHead>Supplier Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Contact Person</TableHead>
              <TableHead>Contact Mobile</TableHead>
              <TableHead>GSTN</TableHead>
              <TableHead>Purchases</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={13} className="text-center align-middle">Loading...</TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} className="text-center align-middle">No suppliers found</TableCell>
              </TableRow>
            ) : (
              paginated.map(supplier => {
                const supplierId = getSupplierId(supplier);
                return (
                  <TableRow 
                    key={supplier.SupplierName + supplier.Phone} 
                    className="align-middle hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleSupplierClick(supplier)}
                  >
                    <TableCell className="align-middle" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={supplierId !== undefined && selectedSuppliers.has(supplierId)}
                        onCheckedChange={() => handleCheckboxChange(supplierId)}
                        aria-label={`Select ${supplier.SupplierName}`}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-middle truncate max-w-[200px]">{supplier.SupplierName}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle truncate max-w-[120px]">{supplier.Phone || '-'}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle truncate max-w-[180px]">{supplier.Email || '-'}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle truncate max-w-[200px]">{supplier.Address || '-'}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle truncate max-w-[120px]">{supplier.State || '-'}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle truncate max-w-[160px]">{supplier.ContactPerson || '-'}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle truncate max-w-[120px]">{supplier.ContactMobile || '-'}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle truncate max-w-[160px]">{supplier.GSTN || '-'}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle text-right">{formatCurrency(supplier.Purchases)}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle text-right">{formatCurrency(supplier.Paid)}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle text-right">{formatCurrency(supplier.Balance)}</TableCell>
                    <TableCell className="whitespace-nowrap align-middle text-right" onClick={(e) => e.stopPropagation()}>
                      {canUpdateSupplier && <Button variant="ghost" size="sm" className="bg-blue-500 hover:bg-blue-600 text-white mr-2" onClick={() => handleEdit(supplier, getSupplierId(supplier))}><Edit className="h-4 w-4" /></Button>}
                      {canDeleteSupplier && <Button variant="ghost" size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => handleDelete(getSupplierId(supplier))}><Trash2 className="h-4 w-4" /></Button>}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {/* Card view for mobile/tablet */}
      <div className="block lg:hidden flex-1 overflow-y-auto p-4" ref={mobileListRef}>
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No suppliers found</div>
          ) : (
            (searchQuery
              ? filtered // show all results if searching
              : filtered.slice(0, mobileLoadedCount) // lazy load if not searching
            ).map(supplier => {
              const supplierId = getSupplierId(supplier);
              return (
                <Card 
                  key={supplier.SupplierName + supplier.Phone} 
                  className="bg-white rounded-lg shadow p-4 border hover:shadow-md transition-shadow duration-200 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSupplierClick(supplier)}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={supplierId !== undefined && selectedSuppliers.has(supplierId)}
                        onCheckedChange={() => handleCheckboxChange(supplierId)}
                        aria-label={`Select ${supplier.SupplierName}`}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-semibold text-lg text-gray-900">{supplier.SupplierName}</div>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          {canUpdateSupplier && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="bg-blue-500 hover:bg-blue-600 text-white p-2" 
                            onClick={() => handleEdit(supplier, getSupplierId(supplier))}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          )}
                          {canDeleteSupplier && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="bg-red-500 hover:bg-red-600 text-white p-2" 
                            onClick={() => handleDelete(getSupplierId(supplier))}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          )}
                        </div>
                      </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">Phone:</span>
                        <span className="text-gray-900">{supplier.Phone || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">Email:</span>
                        <span className="text-gray-900">{supplier.Email || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">Address:</span>
                        <span className="text-gray-900">{supplier.Address || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">State:</span>
                        <span className="text-gray-900">{supplier.State || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">Contact Person:</span>
                        <span className="text-gray-900">{supplier.ContactPerson || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">Contact Mobile:</span>
                        <span className="text-gray-900">{supplier.ContactMobile || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">GSTN:</span>
                        <span className="text-gray-900">{supplier.GSTN || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">Purchases:</span>
                        <span className="text-gray-900 font-semibold">{formatCurrency(supplier.Purchases)}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">Paid:</span>
                        <span className="text-gray-900 font-semibold">{formatCurrency(supplier.Paid)}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-500 font-medium">Balance:</span>
                        <span className={`font-semibold ${(supplier.Balance || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(supplier.Balance)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              );
            })
          )}
          {isMobileLoading && (
            <div className="flex justify-center py-6">
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-sm text-gray-500">Loading more suppliers...</span>
              </div>
            </div>
          )}
          {!isMobileLoading && !searchQuery && mobileLoadedCount < filtered.length && (
            <div className="text-center py-4">
              <div className="mb-3 text-sm text-gray-500">
                Showing {mobileLoadedCount} of {filtered.length} suppliers
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadMoreItems}
                className="text-blue-600 border-blue-600 hover:bg-blue-50"
              >
                Load More Suppliers
              </Button>
              <div className="mt-2 text-xs text-gray-400">
                Scroll down or tap to load more
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Pagination controls - hide on mobile */}
      <div className="hidden lg:flex items-center justify-between mt-4">
        <div className="text-sm text-gray-500">
          Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} records
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</Button>
          <span className="text-sm">Page {currentPage} of {totalPages}</span>
          <Button variant="outline" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</Button>
        </div>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg w-full">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Update Supplier' : 'Add Supplier'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="block font-semibold mb-1 text-red-600">Supplier Name*</label>
              <Input value={form.Name} onChange={e => setForm({ ...form, Name: e.target.value })} required />
            </div>
            <div>
              <label className="block font-semibold mb-1">Email</label>
              <Input value={form.Email} onChange={e => setForm({ ...form, Email: e.target.value })} />
            </div>
            <div>
              <label className="block font-semibold mb-1">Contact Mobile</label>
              <Input value={form.ContactMobile} onChange={e => setForm({ ...form, ContactMobile: e.target.value })} />
            </div>
            <div>
              <label className="block font-semibold mb-1">Phone</label>
              <Input value={form.Phone} onChange={e => setForm({ ...form, Phone: e.target.value })} />
            </div>
            <div>
              <label className="block font-semibold mb-1">Contact Name</label>
              <Input value={form.ContactName} onChange={e => setForm({ ...form, ContactName: e.target.value })} />
            </div>
            <div>
              <label className="block font-semibold mb-1">GSTN</label>
              <Input
                value={form.GSTN}
                onChange={e => {
                  const { value, hadInvalidInput } = sanitizeGstInput(e.target.value);
                  setForm({ ...form, GSTN: value });
                  setGstError(hadInvalidInput);
                }}
                onBlur={() => setGstError(false)}
                maxLength={15}
                className={gstError ? 'border-red-500' : ''}
              />
              {gstError && <p className="text-red-500 text-xs mt-0.5">{GST_VALIDATION_MESSAGE}</p>}
            </div>
            <div>
              <label className="block font-semibold mb-1">Address</label>
              <Input value={form.Address} onChange={e => setForm({ ...form, Address: e.target.value })} />
            </div>
            <div>
              <label className="block font-semibold mb-1">State</label>
              <select name="State" value={form.State} onChange={e => setForm(f => ({ ...f, State: e.target.value }))} className="w-full border rounded px-2 py-1">
                <option value="">{isStatesLoading ? 'Loading...' : 'Select'}</option>
                {statesError && <option value="">Error loading states</option>}
                {states && states.map((state: any) => (
                  <option key={state.Code} value={state.State}>{state.State}</option>
                ))}
              </select>
            </div>
            <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-4 mt-6">
              <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                {isEdit ? 'Update' : 'Submit'}
              </Button>
              <DialogClose asChild>
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="w-full sm:w-auto">
                  Cancel
                </Button>
              </DialogClose>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md w-full">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete this supplier? This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-4">
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              className="w-full sm:w-auto"
              disabled={isDeleting}
            >
              Delete
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteId(undefined);
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierPage; 